import type {
  CodeableConcept,
  Coding,
  Extension,
  Observation,
} from '@medplum/fhirtypes'
import {
  HL7_OBSERVATION_CODE_SYSTEM,
  SEEN_HEALTH_SYSTEM,
} from '#src/codeSystems.ts'
import type { CodingAnswerOption } from '#src/codings.ts'
import type { ConditionEnv } from '#src/observation-logic/evaluate.ts'
import {
  convertAnalyteQuantity,
  convertQuantity,
  roundConverted,
} from '#src/observation-logic/units.ts'
import {
  isEmptyValue,
  type ObservationValue,
} from '#src/observation-logic/value.ts'
import {
  type ResolveBindingOptions,
  type ResourceBindingContext,
  type ResourceBindingId,
  resourceBindingConditionValue,
} from '#src/resourceBindingRegistry.ts'
import { OBSERVATION_CALCULATION_EXTENSION_URL } from '#src/structureDefinitions.ts'

export const CALCULATION_METHOD_SYSTEM = `${SEEN_HEALTH_SYSTEM}/CodeSystem/calculation-method`

// How a rule computes its value, recorded on the result as
// `Observation.method` and the calculation extension.
export interface DerivationMethod {
  code: Coding
  display: string
  formula?: string
  citation?: string
  // Where the citation can be read.
  url?: string
  // When the method applies, or what it assumes.
  note?: string
  version?: string
}

export function calculationMethod(
  code: string,
  display: string,
  extra: Omit<DerivationMethod, 'code' | 'display'> = {}
): DerivationMethod {
  return {
    code: { system: CALCULATION_METHOD_SYSTEM, code, display },
    display,
    ...extra,
  }
}

// A participant or encounter value a rule needs beyond its input
// observations, named by its variable registry binding. `codes` narrows a
// coded binding to the answers the rule can compute with.
export interface DerivationRequirement {
  binding: ResourceBindingId
  codes?: readonly string[]
}

// What a rule computes from: the input values by code, converted to the
// unit asked for, and the registry variables (participant sex, age on the
// encounter day) its requirements name.
export interface DerivationInputs {
  value: (coding: Coding) => ObservationValue | null
  // The input as a number in `unit`; null when absent or not convertible.
  quantity: (coding: Coding, unit: string) => number | null
  // A registry binding's value, read the way conditions and template
  // variables read it; null when it resolves to nothing here.
  variable: (id: ResourceBindingId) => ObservationValue | null
  // What the answer to an item scores: a number answer as itself, a coded
  // one by its option's score. Null when unanswered or unscored.
  score: (coding: Coding) => number | null
  // An item a show-when condition hides, which branching tools count as
  // zero rather than missing.
  hidden: (coding: Coding) => boolean
}

// A value an input takes when neither the note nor the chart has one: a
// customary goal the clinician replaces by recording their own.
export interface DerivationInputDefault {
  coding: Coding
  value: ObservationValue
  // Where the default comes from, shown with the calculation.
  note: string
}

// An input read from the participant's chart history rather than the note:
// the earliest record of `of` in the `withinDays` before the visit, from an
// earlier day. `coding` names it among the rule's inputs; it is never a node.
export interface DerivationHistoryInput {
  coding: Coding
  of: Coding
  withinDays: number
}

export interface DerivationRule {
  id: string
  target: Coding
  inputs: Coding[]
  // Inputs the rule computes without, each left out of what it waits for;
  // `compute` falls back across them (goal weight to current weight).
  optional?: readonly Coding[]
  defaults?: readonly DerivationInputDefault[]
  // Inputs taken together at one sitting (lying, then standing): a record
  // read from the chart counts only from the visit day, so readings from
  // different days are never combined.
  sameVisit?: boolean
  // Inputs among `inputs` read from chart history.
  history?: readonly DerivationHistoryInput[]
  compute: (inputs: DerivationInputs) => ObservationValue | null
  requires?: readonly DerivationRequirement[]
  method?: DerivationMethod
  // The `Observation.category` its record carries.
  category?: ObservationCategoryCode
  // Decimals the result is rounded to in the unit it is shown in. Set when
  // `compute` leaves the value unrounded, so a weight shown in pounds is not
  // rounded once in kilograms and again on conversion.
  precision?: number
}

function sameCoding(a: Coding, b: Coding): boolean {
  return a.code === b.code && (!a.system || !b.system || a.system === b.system)
}

export function isOptionalInput(rule: DerivationRule, coding: Coding): boolean {
  return (rule.optional ?? []).some((input) => sameCoding(input, coding))
}

export function inputDefault(
  rule: DerivationRule,
  coding: Coding
): DerivationInputDefault | undefined {
  return rule.defaults?.find((entry) => sameCoding(entry.coding, coding))
}

export function historyInput(
  rule: DerivationRule,
  coding: Coding
): DerivationHistoryInput | undefined {
  return rule.history?.find((entry) => sameCoding(entry.coding, coding))
}

// The inputs a rule still waits for, given each input's value: a required
// one with none, else, when the optional ones it falls back across all
// lack one too, those.
export function waitingInputs(
  rule: DerivationRule,
  values: readonly DerivationInputValue[]
): Coding[] {
  const absent = rule.inputs.filter(
    (coding) =>
      (values.find((entry) => sameCoding(entry.coding, coding))?.value ??
        null) === null
  )
  const required = absent.filter((coding) => !isOptionalInput(rule, coding))
  if (required.length) {
    return required
  }
  const optional = rule.optional ?? []
  return optional.length &&
    optional.every((coding) =>
      absent.some((missing) => sameCoding(missing, coding))
    )
    ? absent
    : []
}

// A rule's result in `unit` when the two convert, rounded once there.
export function derivedValueInUnit(
  rule: Pick<DerivationRule, 'precision'>,
  value: ObservationValue | null,
  unit: string | null | undefined
): ObservationValue | null {
  if (value?.kind !== 'number') {
    return value
  }
  const converted =
    value.unit && unit && value.unit !== unit
      ? convertQuantity(value.value, value.unit, unit)
      : null
  if (converted === null) {
    return rule.precision === undefined
      ? value
      : { ...value, value: roundConverted(value.value, rule.precision) }
  }
  return {
    ...value,
    value: roundConverted(converted, rule.precision),
    unit: unit ?? value.unit,
  }
}

export type ObservationCategoryCode =
  | 'vital-signs'
  | 'laboratory'
  | 'survey'
  | 'exam'

const OBSERVATION_CATEGORY_DISPLAY: Readonly<
  Record<ObservationCategoryCode, string>
> = {
  'vital-signs': 'Vital Signs',
  laboratory: 'Laboratory',
  survey: 'Survey',
  exam: 'Exam',
}

// `Observation.category` for an HL7 observation-category code.
export function observationCategory(
  code: ObservationCategoryCode
): CodeableConcept[] {
  return [
    {
      coding: [
        {
          system: HL7_OBSERVATION_CODE_SYSTEM,
          code,
          display: OBSERVATION_CATEGORY_DISPLAY[code],
        },
      ],
    },
  ]
}

export interface DerivationInputValue {
  coding: Coding
  value: ObservationValue | null
}

function sameConcept(a: Coding, b: Coding): boolean {
  return a.code === b.code && (!a.system || !b.system || a.system === b.system)
}

export interface DerivationInputOptions {
  // Reads registry bindings; see `contextVariables`.
  variable?: ConditionEnv['variable']
  // The answers an item offers, with their scores.
  answerOptions?: (item: Coding) => readonly CodingAnswerOption[] | undefined
  // Items hidden by their show-when condition.
  hidden?: readonly Coding[]
}

export function derivationInputs(
  values: readonly DerivationInputValue[],
  options: DerivationInputOptions
): DerivationInputs {
  const value = (coding: Coding): ObservationValue | null =>
    values.find((candidate) => sameConcept(candidate.coding, coding))?.value ??
    null
  return {
    value,
    quantity: (coding, unit) => {
      const found = value(coding)
      if (found?.kind !== 'number') {
        return null
      }
      if (!found.unit) {
        return found.value
      }
      return convertAnalyteQuantity(coding.code, found.value, found.unit, unit)
    },
    variable: (id) => {
      const found = options.variable?.(id) ?? null
      return found === 'unavailable' ? null : found
    },
    score: (coding) => {
      const found = value(coding)
      if (found?.kind === 'number') {
        return found.value
      }
      if (found?.kind !== 'coding') {
        return null
      }
      const answers = options.answerOptions?.(coding) ?? []
      const scores = found.value.flatMap((answer) => {
        const option = answers.find(
          (candidate) =>
            candidate.code === answer.code &&
            (!candidate.system ||
              !answer.system ||
              candidate.system === answer.system)
        )
        return option?.score === undefined ? [] : [option.score]
      })
      return scores.length
        ? scores.reduce((sum, score) => sum + score, 0)
        : null
    },
    hidden: (coding) =>
      options.hidden?.some((candidate) => sameConcept(candidate, coding)) ??
      false,
  }
}

// The rules that compute this code, in the order they are offered.
export function rulesForTarget(
  target: Coding,
  rules: readonly DerivationRule[]
): DerivationRule[] {
  return rules.filter((rule) => sameConcept(rule.target, target))
}

// The chosen rule for this code, else its first.
export function selectedRule(
  target: Coding,
  rules: readonly DerivationRule[],
  chosenRuleId?: string
): DerivationRule | undefined {
  const alternatives = rulesForTarget(target, rules)
  return (
    alternatives.find((rule) => rule.id === chosenRuleId) ?? alternatives[0]
  )
}

// Registry bindings read for a participant and encounter, the same path
// show-when conditions and template variables resolve through. An age is
// for the encounter's start day, else `today`.
export function contextVariables(
  context: ResourceBindingContext,
  options: ResolveBindingOptions = {}
): ConditionEnv['variable'] {
  return (id) => resourceBindingConditionValue(id, context, options)
}

// The bindings a rule requires that resolve to nothing usable here, in the
// order the rule declares them.
export function missingRequirements(
  rule: DerivationRule | undefined,
  inputs: DerivationInputs
): ResourceBindingId[] {
  return (rule?.requires ?? []).flatMap(({ binding, codes }) => {
    const value = inputs.variable(binding)
    const usable =
      !!value &&
      !isEmptyValue(value) &&
      (!codes ||
        (value.kind === 'coding' &&
          value.value.some((coding) => codes.includes(coding.code ?? ''))))
    return usable ? [] : [binding]
  })
}

// The observation with how it was calculated: the method coding and the
// calculation extension (formula, rule id, version, input codes). Returns
// it unchanged when the rule records no method.
export function applyCalculationMethod(
  observation: Observation,
  rule: DerivationRule
): Observation {
  const { method } = rule
  if (!method) {
    return observation
  }
  const parts: Extension[] = [
    ...(method.formula
      ? [{ url: 'formula', valueString: method.formula }]
      : []),
    { url: 'library', valueString: rule.id },
    ...(method.version
      ? [{ url: 'version', valueString: method.version }]
      : []),
    ...(rule.inputs.length
      ? [
          {
            url: 'inputs',
            valueString: rule.inputs.map((coding) => coding.code).join(','),
          },
        ]
      : []),
  ]
  return {
    ...observation,
    method: { coding: [{ ...method.code }], text: method.display },
    extension: [
      ...(observation.extension ?? []).filter(
        (extension) => extension.url !== OBSERVATION_CALCULATION_EXTENSION_URL
      ),
      { url: OBSERVATION_CALCULATION_EXTENSION_URL, extension: parts },
    ],
  }
}
